import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiSend,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertTriangle,
  FiEye,
  FiEdit2,
  FiSlash,
  FiCheck,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import {
  APPLICANTS_QUERY,
  JOB_OFFERS_QUERY,
  CREATE_JOB_OFFER,
  UPDATE_JOB_OFFER,
  UPDATE_JOB_OFFER_STATUS,
  applicantInitials,
  formatAppliedDate,
  mapApplicant,
} from '../data/recruitmentApi';
import '../styles/recruitmentShared.css';
import '../styles/AddApplicant.css';
import '../styles/JobOffers.css';

function daysRemaining(expiresAt) {
  if (!expiresAt) return null;
  const end = new Date(expiresAt);
  if (Number.isNaN(end.getTime())) return null;
  return Math.ceil((end.getTime() - Date.now()) / 86400000);
}

function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const EMPTY_FORM = {
  applicantId: '',
  name: '',
  role: '',
  salary: '',
  branch: '',
  offerDate: '',
  expiresAt: '',
  notes: '',
  status: 'pending',
};

const STATUS_LABEL = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
};

export default function JobOffers() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const overlay = useAdminListOverlay(offers);
  const active = overlay.editing || overlay.viewing;
  const mode = overlay.showAdd
    ? 'create'
    : overlay.editing
      ? 'edit'
      : overlay.viewing
        ? 'view'
        : null;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const [offerData, applicantData] = await Promise.all([
        graphqlRequest(JOB_OFFERS_QUERY),
        graphqlRequest(APPLICANTS_QUERY),
      ]);
      setOffers(
        Array.isArray(offerData?.recruitmentJobOffers)
          ? offerData.recruitmentJobOffers
          : [],
      );
      setApplicants(
        Array.isArray(applicantData?.recruitmentApplicants)
          ? applicantData.recruitmentApplicants.map(mapApplicant)
          : [],
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to load job offers.');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOffers();
  }, [fetchOffers]);

  const stats = useMemo(() => {
    const expiring = offers.filter((o) => {
      if (o.status !== 'pending') return false;
      const d = daysRemaining(o.expiresAt);
      return d !== null && d >= 0 && d <= 3;
    }).length;
    return [
      { id: 'sent', label: 'Offers Sent', value: offers.length, icon: FiSend, tone: 'blue' },
      {
        id: 'accepted',
        label: 'Accepted',
        value: offers.filter((o) => o.status === 'accepted').length,
        icon: FiCheckCircle,
        tone: 'green',
      },
      {
        id: 'rejected',
        label: 'Rejected',
        value: offers.filter((o) => o.status === 'rejected').length,
        icon: FiXCircle,
        tone: 'red',
      },
      {
        id: 'pending',
        label: 'Pending',
        value: offers.filter((o) => o.status === 'pending').length,
        icon: FiClock,
        tone: 'yellow',
      },
      {
        id: 'expiring',
        label: 'Offer Expiring',
        value: expiring,
        icon: FiAlertTriangle,
        tone: 'orange',
      },
    ];
  }, [offers]);

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  useEffect(() => {
    if (overlay.showAdd) {
      setForm(EMPTY_FORM);
      return;
    }
    const offer = overlay.editing;
    if (!offer) return;
    setForm({
      applicantId: offer.applicantId || '',
      name: offer.name || '',
      role: offer.role || '',
      salary: offer.salary || '',
      branch: offer.branch || '',
      offerDate: toDateInput(offer.offerDate),
      expiresAt: toDateInput(offer.expiresAt),
      notes: offer.notes || '',
      status: offer.status || 'pending',
    });
  }, [overlay.showAdd, overlay.editing]);

  const openCreate = () => overlay.openAdd();
  const openView = (offer) => overlay.openView(offer);
  const openEdit = (offer) => overlay.openEdit(offer);
  const closeModal = () => overlay.closeOverlay();

  const onPickApplicant = (id) => {
    const a = applicants.find((x) => x.id === id);
    setForm((prev) => ({
      ...prev,
      applicantId: id,
      name: a?.name || prev.name,
      role: a?.role || prev.role,
      branch: a?.branch || prev.branch,
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim()) {
      toast.error('Name and role are required.');
      return;
    }
    setSaving(true);
    try {
      await graphqlRequest(CREATE_JOB_OFFER, {
        input: {
          applicantId: form.applicantId || null,
          name: form.name.trim(),
          role: form.role.trim(),
          salary: form.salary.trim() || null,
          branch: form.branch.trim() || null,
          offerDate: form.offerDate || null,
          expiresAt: form.expiresAt || null,
          notes: form.notes.trim() || null,
          status: 'pending',
        },
      });
      toast.success('Offer created.');
      closeModal();
      await fetchOffers();
    } catch (err) {
      toast.error(err?.message || 'Could not create offer.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!active?.id) return;
    if (!form.name.trim() || !form.role.trim()) {
      toast.error('Name and role are required.');
      return;
    }
    setSaving(true);
    try {
      await graphqlRequest(UPDATE_JOB_OFFER, {
        id: active.id,
        input: {
          name: form.name.trim(),
          role: form.role.trim(),
          salary: form.salary.trim() || null,
          branch: form.branch.trim() || null,
          offerDate: form.offerDate || null,
          expiresAt: form.expiresAt || null,
          notes: form.notes.trim() || null,
          status: form.status,
        },
      });
      toast.success('Offer updated.');
      closeModal();
      await fetchOffers();
    } catch (err) {
      toast.error(err?.message || 'Could not update offer.');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await graphqlRequest(UPDATE_JOB_OFFER_STATUS, { id, status });
      toast.success(
        status === 'accepted'
          ? 'Offer accepted. Candidate moved to Background Check.'
          : status === 'rejected'
            ? 'Offer rejected.'
            : 'Offer updated.',
      );
      await fetchOffers();
    } catch (err) {
      toast.error(err?.message || 'Could not update offer.');
    }
  };

  return (
    <div className="rm-shared jo-page">
      <nav className="rm-crumb" aria-label="Breadcrumb">
        <button
          type="button"
          className="rm-crumb-back"
          onClick={() =>
            navigate({ pathname: '/', search: '?page=recruitment' })
          }
        >
          Recruitment
        </button>
        <span className="rm-crumb-sep">/</span>
        <span>Job Offers</span>
      </nav>

      <header className="rm-page-header">
        <div>
          <h1 className="rm-page-title">Job Offers</h1>
          <p className="rm-page-subtitle">
            Track, send, and manage all employment offers.
          </p>
        </div>
        <button
          type="button"
          className="rm-btn rm-btn--primary"
          onClick={openCreate}
        >
          <FiPlus />
          Create Offer
        </button>
      </header>

      <section className="jo-stats">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.id} className="rm-stat-card">
              <div className={`rm-stat-icon rm-stat-icon--${s.tone}`}>
                <Icon />
              </div>
              <div>
                <div className="rm-stat-value">{s.value}</div>
                <div className="rm-stat-label">{s.label}</div>
              </div>
            </article>
          );
        })}
      </section>

      {loading ? (
        <div className="jo-empty">Loading offers…</div>
      ) : offers.length === 0 ? (
        <div className="jo-empty">
          No job offers yet. Click Create Offer to add one.
        </div>
      ) : (
        <div className="jo-grid">
          {offers.map((offer) => {
            const remaining = daysRemaining(offer.expiresAt);
            return (
              <article key={offer.id} className="jo-card">
                <div className="jo-card-top">
                  <div className="rm-applicant-cell">
                    <div
                      className="rm-avatar"
                      style={{ background: offer.avatarColor || '#60a5fa' }}
                    >
                      {applicantInitials(offer.name)}
                    </div>
                    <div>
                      <p className="rm-applicant-name">{offer.name}</p>
                      <p className="rm-applicant-id">{offer.role}</p>
                    </div>
                  </div>
                  <span className={`jo-status jo-status--${offer.status}`}>
                    {STATUS_LABEL[offer.status] || offer.status}
                  </span>
                </div>

                <div className="jo-details">
                  <div>
                    <span>Annual salary</span>
                    <strong>{offer.salary || '—'}</strong>
                  </div>
                  <div>
                    <span>Branch</span>
                    <strong>{offer.branch || '—'}</strong>
                  </div>
                  <div>
                    <span>Offer Date</span>
                    <strong>{formatAppliedDate(offer.offerDate)}</strong>
                  </div>
                  <div>
                    <span>Expires</span>
                    <strong>{formatAppliedDate(offer.expiresAt)}</strong>
                  </div>
                </div>

                {offer.status === 'pending' &&
                remaining !== null &&
                remaining >= 0 ? (
                  <div className="jo-remaining">
                    <FiClock />
                    {remaining} day{remaining === 1 ? '' : 's'} remaining
                  </div>
                ) : null}

                <div className="jo-actions">
                  <button
                    type="button"
                    className="jo-action"
                    onClick={() => openView(offer)}
                  >
                    <FiEye /> View
                  </button>
                  <button
                    type="button"
                    className="jo-action"
                    onClick={() => openEdit(offer)}
                  >
                    <FiEdit2 /> Edit
                  </button>
                  {offer.status === 'pending' ? (
                    <>
                      <button
                        type="button"
                        className="jo-action jo-action--ok"
                        onClick={() => void setStatus(offer.id, 'accepted')}
                      >
                        <FiCheck /> Accept
                      </button>
                      <button
                        type="button"
                        className="jo-action jo-action--danger"
                        onClick={() => void setStatus(offer.id, 'rejected')}
                      >
                        <FiX /> Reject
                      </button>
                      <button
                        type="button"
                        className="jo-action jo-action--danger"
                        onClick={() => void setStatus(offer.id, 'withdrawn')}
                      >
                        <FiSlash /> Withdraw
                      </button>
                    </>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {mode === 'view' && active ? (
        <div className="aa-overlay" onClick={closeModal}>
          <div className="aa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aa-header">
              <div>
                <h2>Offer details</h2>
                <p>
                  {active.name} · {active.role}
                </p>
              </div>
              <button type="button" className="aa-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="jo-details" style={{ marginBottom: 16 }}>
              <div>
                <span>Status</span>
                <strong>{STATUS_LABEL[active.status] || active.status}</strong>
              </div>
              <div>
                <span>Salary</span>
                <strong>{active.salary || '—'}</strong>
              </div>
              <div>
                <span>Branch</span>
                <strong>{active.branch || '—'}</strong>
              </div>
              <div>
                <span>Offer date</span>
                <strong>{formatAppliedDate(active.offerDate)}</strong>
              </div>
              <div>
                <span>Expires</span>
                <strong>{formatAppliedDate(active.expiresAt)}</strong>
              </div>
              <div>
                <span>Notes</span>
                <strong>{active.notes || '—'}</strong>
              </div>
            </div>
            <div className="aa-actions">
              <button
                type="button"
                className="aa-btn aa-btn--ghost"
                onClick={closeModal}
              >
                Close
              </button>
              <button
                type="button"
                className="aa-btn aa-btn--primary"
                onClick={() => openEdit(active)}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {mode === 'create' || mode === 'edit' ? (
        <div className="aa-overlay" onClick={closeModal}>
          <div className="aa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aa-header">
              <div>
                <h2>{mode === 'create' ? 'Create Offer' : 'Edit Offer'}</h2>
                <p>
                  {mode === 'create'
                    ? 'Send a new employment offer to a candidate.'
                    : 'Update offer details and status.'}
                </p>
              </div>
              <button type="button" className="aa-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form
              className="aa-form"
              onSubmit={mode === 'create' ? handleCreate : handleEdit}
            >
              {mode === 'create' ? (
                <label className="aa-field aa-field--full">
                  <span>Applicant</span>
                  <select
                    value={form.applicantId}
                    onChange={(e) => onPickApplicant(e.target.value)}
                  >
                    <option value="">Select applicant (optional)</option>
                    {applicants.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} — {a.role}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <div className="aa-grid">
                <label className="aa-field">
                  <span>Candidate name *</span>
                  <input
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    required
                  />
                </label>
                <label className="aa-field">
                  <span>Job role *</span>
                  <input
                    value={form.role}
                    onChange={(e) => setField('role', e.target.value)}
                    required
                  />
                </label>
                <label className="aa-field">
                  <span>Annual salary (p.a.)</span>
                  <input
                    value={form.salary}
                    onChange={(e) => setField('salary', e.target.value)}
                    placeholder="$75,000"
                  />
                </label>
                <label className="aa-field">
                  <span>Branch</span>
                  <input
                    value={form.branch}
                    onChange={(e) => setField('branch', e.target.value)}
                  />
                </label>
                <label className="aa-field">
                  <span>Offer date</span>
                  <input
                    type="date"
                    value={form.offerDate}
                    onChange={(e) => setField('offerDate', e.target.value)}
                  />
                </label>
                <label className="aa-field">
                  <span>Expires</span>
                  <input
                    type="date"
                    value={form.expiresAt}
                    onChange={(e) => setField('expiresAt', e.target.value)}
                  />
                </label>
                {mode === 'edit' ? (
                  <label className="aa-field">
                    <span>Status</span>
                    <select
                      value={form.status}
                      onChange={(e) => setField('status', e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                      <option value="withdrawn">Withdrawn</option>
                      <option value="expired">Expired</option>
                    </select>
                  </label>
                ) : null}
              </div>

              <label className="aa-field aa-field--full">
                <span>Notes</span>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                />
              </label>

              <div className="aa-actions">
                <button
                  type="button"
                  className="aa-btn aa-btn--ghost"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="aa-btn aa-btn--primary"
                  disabled={saving}
                >
                  {saving
                    ? 'Saving…'
                    : mode === 'create'
                      ? 'Save Offer'
                      : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
